import {
  Button,
  Divider,
  HStack,
  Link,
  ListItem,
  Stack,
  Text,
  UnorderedList,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { TrackedButton } from '$/components/common/Button/TrackedButton';
import { Icon } from '$/components/common/Icon';
import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import {
  HookFormCheckbox,
  type HookFormCheckboxProps,
} from '$/components/core/Form/HookFormCheckbox';
import {
  HookFormDropdown,
  type HookFormDropdownProps,
} from '$/components/core/Form/HookFormDropdown';
import {
  HookFormInput,
  type HookFormInputProps,
} from '$/components/core/Form/HookFormInput';
import { StyledLink } from '$/components/core/StyledLink';
import { useCloudMessageTranslation } from '$/hooks/useCloudMessageTranslation';
import { registerUser } from '$/services/usecases/authentication';
import { emailFormatRegex, isPasswordValid } from '$/utils/regexUtils';

const SalutationMap: Record<string, string> = {
  m: 'male',
  f: 'female',
  u: 'nonBinary',
};

export type RegisterData = {
  salutation?: keyof typeof SalutationMap;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  acceptedTerms: boolean;
};

const RegisterInput = (props: HookFormInputProps<RegisterData>) => (
  <HookFormInput<RegisterData> {...props} />
);

const RegisterCheckbox = (props: HookFormCheckboxProps<RegisterData>) => (
  <HookFormCheckbox<RegisterData> {...props} />
);

const RegisterDropdown = (props: HookFormDropdownProps<RegisterData>) => (
  <HookFormDropdown<RegisterData> {...props} />
);

export const RegisterForm = () => {
  const { t } = useTranslation();

  const formMethods = useForm<RegisterData>({
    mode: 'onTouched',
    delayError: 100,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { cloudMessageTranslation } = useCloudMessageTranslation();
  const salutationDropdown = useDisclosure();

  const setModalType = useAuthenticationStore.useSetModalType();
  const setPendingAuthEmail = useAuthenticationStore.useSetPendingAuthEmail();
  const setPendingAuthToken = useAuthenticationStore.useSetPendingAuthToken();
  const setAuthenticationModalType = useAuthenticationStore.useSetModalType();

  const { mutate: onRegister, isPending } = useMutation({
    mutationFn: registerUser,
    onSuccess: (data) => {
      const { response, isSuccessful } = data;

      if (isSuccessful && response.user && response.otpToken) {
        setPendingAuthEmail(response.user.email);
        setPendingAuthToken(response.otpToken);
        setModalType('registerOtp');
      } else {
        setError(cloudMessageTranslation(response.message));
      }
    },
    onError: () => {
      setError(cloudMessageTranslation('genericError'));
    },
  });

  // eslint is lying, it's another type. Recommended by https://www.i18next.com/overview/typescript#example-with-array
  const featureList = t('dashboard.authentication.signUpAdvantages', {
    returnObjects: true,
  }) as string[];

  return (
    <FormProvider {...formMethods}>
      <VStack
        as='form'
        gap='10'
        onSubmit={formMethods.handleSubmit((data) => onRegister(data))}
      >
        <Stack gap='6'>
          <Stack gap='4' fontSize='sm'>
            <Text>{t('dashboard.authentication.signUpWelcomeText')}</Text>
            <UnorderedList ml='4'>
              {featureList.map((advantage) => (
                <ListItem key={advantage}>{advantage}</ListItem>
              ))}
            </UnorderedList>
          </Stack>
          <RegisterDropdown
            dropdownOpen={salutationDropdown.isOpen}
            toggleDropdown={salutationDropdown.onToggle}
            accessor='salutation'
            valueLabels={{
              u: t('dashboard.authentication.salutation.nonBinary'),
              m: t('dashboard.authentication.salutation.male'),
              f: t('dashboard.authentication.salutation.female'),
            }}
            placeholder={t('dashboard.authentication.defaultPlaceholder', {
              field: t('dashboard.authentication.salutationLabel'),
            })}
            defaultValue={undefined}
            label={t('dashboard.authentication.salutationLabel')}
            dropdownContent={() => {
              return (
                <Stack pt='2' pb='6px'>
                  {Object.entries(SalutationMap).map(([key, label]) => (
                    <Button
                      key={key}
                      px='4'
                      py='6px'
                      fontSize='sm'
                      fontWeight='normal'
                      textAlign='left'
                      _hover={{ bg: 'bodyBackground' }}
                      cursor='pointer'
                      onClick={(e) => {
                        if (formMethods.getValues().salutation !== key) {
                          formMethods.setValue('salutation', key);
                        }
                        salutationDropdown.onClose();
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      variant='unstyled'
                    >
                      {t(`dashboard.authentication.salutation.${label}`)}
                    </Button>
                  ))}
                </Stack>
              );
            }}
          />
          <HStack gap='4'>
            <RegisterInput
              accessor='firstName'
              label={t('dashboard.authentication.firstNameLabel')}
              placeholder={t('dashboard.authentication.defaultPlaceholder', {
                field: t('dashboard.authentication.firstNameLabel'),
              })}
              registerOptions={{
                required: t(
                  'dashboard.authentication.errorMessages.isRequired',
                ),
              }}
              hideErrorLabel
            />
            <RegisterInput
              accessor='lastName'
              label={t('dashboard.authentication.lastNameLabel')}
              placeholder={t('dashboard.authentication.defaultPlaceholder', {
                field: t('dashboard.authentication.lastNameLabel'),
              })}
              registerOptions={{
                required: t(
                  'dashboard.authentication.errorMessages.isRequired',
                ),
              }}
              hideErrorLabel
            />
          </HStack>
          <RegisterInput
            type='email'
            accessor='email'
            label={t('dashboard.authentication.emailLabel')}
            placeholder={t('dashboard.authentication.defaultPlaceholder', {
              field: t('dashboard.authentication.emailLabel'),
            })}
            registerOptions={{
              required: t('dashboard.authentication.errorMessages.isRequired'),
              pattern: {
                value: emailFormatRegex,
                message: t(
                  'dashboard.authentication.errorMessages.wrongFormat',
                ),
              },
            }}
            description={t('dashboard.authentication.errorMessages.emailRules')}
          />
          <RegisterInput
            type={showPassword ? 'text' : 'password'}
            accessor='password'
            label={t('dashboard.authentication.insertPasswordLabel')}
            placeholder={t('dashboard.authentication.defaultPlaceholder', {
              field: t('dashboard.authentication.passwordLabel'),
            })}
            registerOptions={{
              required: t('dashboard.authentication.errorMessages.isRequired'),
              validate: {
                passwordValid: (v) =>
                  isPasswordValid(String(v)) ||
                  t(
                    'dashboard.authentication.errorMessages.passwordRulesMismatch',
                  ),
              },
            }}
            inputRightElement={
              <Button
                onClick={() => setShowPassword((state) => !state)}
                variant='text'
              >
                <Icon
                  icon={showPassword ? 'visibility_on' : 'visibility_off'}
                />
              </Button>
            }
            description={t(
              'dashboard.authentication.errorMessages.passwordRules',
            )}
          />
          {error && (
            <Text color='form.errorText' fontWeight='bold'>
              {error}
            </Text>
          )}
          <HStack alignItems='flex-start' gap='3'>
            <RegisterCheckbox
              accessor='acceptedTerms'
              registerOptions={{ validate: (val) => val === true }}
              customLabelContent={
                <Text mt='-1' fontSize='xs'>
                  <Trans
                    i18nKey='dashboard.authentication.createAccountLabel'
                    components={{
                      TermsLink: (
                        <StyledLink
                          color='primaryButton.background'
                          textDecoration='underline'
                          to='/tos'
                        >
                          Link
                        </StyledLink>
                      ),
                    }}
                  />
                </Text>
              }
            />
          </HStack>
          <Text fontSize='xs'>
            <Trans
              i18nKey='dashboard.authentication.createAccountLabelPrivacy'
              components={{
                PrivacyLink: (
                  <StyledLink
                    color='primaryButton.background'
                    textDecoration='underline'
                    to='/privacy'
                  >
                    Link
                  </StyledLink>
                ),
              }}
            />
          </Text>
        </Stack>

        <Stack gap='6' w='full'>
          <TrackedButton
            fontSize='sm'
            isDisabled={!formMethods.formState.isValid}
            isLoading={isPending}
            type='submit'
            contentName='CloudAccount'
            contentPiece='Registration'
            contentTarget='NewAccount'
          >
            {t('dashboard.authentication.createAccountButton')}
          </TrackedButton>

          <Divider borderColor='border' />

          <HStack justifyContent='center' gap='1' fontSize='sm'>
            <Text>{t('dashboard.authentication.alreadyAccount')}</Text>
            <Link
              textColor={'primaryButton.background'}
              fontWeight='bold'
              onClick={() => setAuthenticationModalType('login')}
            >
              {t('dashboard.authentication.registerNow')}
            </Link>
          </HStack>
        </Stack>
      </VStack>
    </FormProvider>
  );
};
